import { RestQueryParamsEncoder, RestQueryParamsEncoderJson } from "./query-params-encoder";
import {
    Headers, Http, Request, RequestMethod, RequestOptionsArgs, Response,
    ResponseContentType
} from "@angular/http";
import { AuthHttp } from "angular2-jwt";
import { Observable } from "rxjs/Observable";
import { RequestArgs } from "@angular/http/src/interfaces";
import { isArray, isDefined, isObject, isString } from "ionic-angular/util/util";
import { HTTP_METHODS } from "../sync-up/index";

export class RestRequest {

    protected since: Date;
    protected queryParamsEncoder: RestQueryParamsEncoder;
    protected queryParams: object;
    protected path: string;
    protected host: string;
    protected endpoint: string;
    protected body: any;
    protected method: RequestMethod;
    protected debugRestCalls: boolean = false;
    protected transport: Http | AuthHttp;
    protected responseType: ResponseContentType;
    protected headers: Headers = new Headers();
    protected defaultHeaders = [
        { name: "Content-type", value: "application/json"},
        { name: "Accept", value: "application/json"}
    ];

    constructor(transport: Http | AuthHttp) {
        this.transport = transport;
        this.queryParamsEncoder = new RestQueryParamsEncoderJson();
        this.initHeaders();
    }

    protected initHeaders(): void {
        this.defaultHeaders.forEach(header => {
            this.headers.append(header.name, header.value);
        });
    }

    getQuery(): string {

        let parts: Array<string> = [];

        if (this.queryParams && Object.keys(this.queryParams).length) {
            parts.push(`${this.queryParamsEncoder.getQuery(this.queryParams)}`);
            parts = Object.keys(this.queryParams).map(key => {
                switch (true) {
                    case isArray(this.queryParams[key]): {
                        return `${key}=${this.queryParams[key].join(",")}`;
                    }
                    case isObject(this.queryParams[key]): {
                        return `${key}=${JSON.stringify(this.queryParams[key])}`;
                    }
                    default: {
                        return `${key}=${this.queryParams[key]}`;
                    }
                }
            });
        }

        if (this.debugRestCalls) {
            parts.push(`XDEBUG_SESSION_START=PHPSTORM`);
        }

        if (parts.length) {
            return `?${parts.join("&")}`;
        } else {
            return "";
        }
    }

    setQueryParams(query: object): RestRequest {
        this.queryParams = query;
        return this;
    }

    getQueryParams(): object {
        return this.queryParams;
    }

    setHost(host: string): RestRequest {
        this.host = host;
        return this;
    }

    getHost(): string {
        return this.host;
    }

    setEndpoint(endpoint: string): RestRequest {
        this.endpoint = endpoint;
        return this;
    }

    getEndpoint(): string {
        return this.endpoint;
    }

    setDebugRoute(enabled: boolean): RestRequest {
        this.debugRestCalls = enabled;
        return this;
    }

    getDebugRoute(): boolean {
        return this.debugRestCalls;
    }

    setBody(body: any): RestRequest {
        this.body = body;
        return this;
    }

    getBody(): any {
        return this.body;
    }

    setPath(path: string): RestRequest {
        this.path = path;
        return this;
    }

    getPath(): string {
        return this.path;
    }

    setMethod(method: string | RequestMethod): RestRequest {

        if (isString(method)) {
            this.setMethodByString(method);
        } else {
            this.method = method;
        }
        return this;
    }

    setMethodByString(method: string): RestRequest {

        let m: RequestMethod;

        switch (method) {
            case HTTP_METHODS.GET: {
                m = RequestMethod.Get;
                break;
            }
            case HTTP_METHODS.PATCH: {
                m = RequestMethod.Patch;
                break;
            }
            case HTTP_METHODS.POST: {
                m = RequestMethod.Post;
                break;
            }
            default: {
                throw Error(`invalid method '${method}'`);
            }
        }

        this.method = m;
        return this;
    }

    getMethod(): RequestMethod {
        return this.method;
    }

    getHeaders(): Headers {
        return this.headers;
    }

    appendHeader(key: string, value: string): RestRequest {

        // see https://github.com/angular/angular/issues/11819
        if (key.toLowerCase() === "content-type" && this.headers.has(key)) {
            this.headers.delete(key);
        }

        this.headers.append(key, value);
        return this;
    }

    appendHeaders(headers: Headers): RestRequest {

        headers.keys().forEach(key => {

            if (this.headers.has(key)) {
                this.headers.set(key, headers.getAll(key));
            } else {
                headers.getAll(key).forEach(value => {
                    this.headers.append(key, value);
                });
            }
        });
        return this;
    }

    pluralise(value: string): string {
        if (value.substring(value.length - 1) === "s") {
            return `${value}es`;
        } else {
            return `${value}s`;
        }
    }

    getUrl(): string {

        let path = this.path || "";

        [this.host, this.endpoint].filter(part => !!part).forEach(s => {
            path = path.replace(s, "");
        });

        path = path.replace(/^\//, "").replace(/\/$/, "");

        const base = [
            this.host,
            this.endpoint,
            this.path
        ]
            .filter(part => !!part)
            .map(part => part.replace(/^\//, ""))
            .join("/");

        const route = `${base.toLowerCase()}${this.getQuery()}`;

        return route.replace(/\/$/, "");
    }

    getRequestOptionsArgs(): RequestOptionsArgs {

        if (!isDefined(this.method)) {
            throw Error("method must be set");
        }

        return {
            method: this.getMethod(),
            search: undefined,
            params: undefined,
            headers: this.getHeaders(),
            body: this.getBody(),
            withCredentials: undefined,
            responseType: this.getResponseType()
        };
    }

    getRequestArgs(): RequestArgs {
        return {
            ...this.getRequestOptionsArgs(),
            url: this.getUrl()
        };
    }

    setResponseType(type: ResponseContentType): RestRequest {
        this.responseType = type;
        return this;
    }

    getResponseType(): ResponseContentType {
        return this.responseType || ResponseContentType.Json;
    }

    getRequest(): Request {
        return new Request(this.getRequestArgs());
    }

    execute(): Observable<Response> {
        return this.transport.request(this.getRequest());
    }
}



// WEBPACK FOOTER //
// ./src/core/services/api/request.ts